// src/pages/HomeView/components/NewsFeed/DueTimelineSection.tsx
import React, { useState, useEffect } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { AlertCircle, LayoutGrid, CalendarDays, CalendarRange } from 'lucide-react';
import { homeService } from '../../../../services/homeService';
import { authStorage } from '../../../../lib/storage';
import { config as envConfig } from '@/config/env';
import ConversationDetailsView from '@/components/ConversationDetails/ConversationDetailsView';
import type {
  ConversationDetail as ConversationDetailType,
  DashboardGroupConversationsResponse,
} from '../../../../services/homeService';
import type { ApiConfig, AppConfig } from '@/components/ConversationItem/types';
import { conversationBus } from '@/lib/conversationBus';
import { readViewCache, writeViewCache } from '@/lib/viewCache';

const DUETIMELINE_CACHE_KEY = 'C2_Cache_Home_DueTimeline';

/** Cards cached per group: enough for the first screen. */
const DUETIMELINE_CACHE_LIMIT = 15;

// Helper function to get dynamic API configuration from session
const getApiConfig = (): ApiConfig & {
  refreshToken: string;
  ExternalData: string;
  SocketServer: string;
  TenancyName: string;
  PersonId: string;
} =>
{
  const token = authStorage.getAccessToken() || '';
  const personId = authStorage.getPersonId() || '0';

  return {
    url: envConfig.api.baseUrl,
    token: token,
    refreshToken: '',
    ExternalData: '',
    SocketServer: envConfig.socket.server,
    TenancyName: envConfig.tenant.defaultName,
    PersonId: personId,
  };
};

// Helper function to get dynamic App configuration
const getAppConfig = (): AppConfig & {
  buildVersion?: string;
  webChaturl?: string;
  appPrefix?: string;
} =>
{
  const webUrl = import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/';
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost/Corgee.Web/';
  return {
    weburl: webUrl,
    apiurl: apiUrl,
    buildVersion: '1.0.0',
    webChaturl: webUrl + '/CHAT',
    appPrefix: '',
  };
};

// Storage Data Configuration - reads from real session/localStorage
const getStorageData = () =>
{
  let data: Record<string, any> = {};
  try
  {
    data = JSON.parse(sessionStorage.getItem('C2_data') || '{}');
  }
  catch
  {
    /* ignore */
  }

  let allLayers: any[] = [];
  try
  {
    allLayers = JSON.parse(sessionStorage.getItem('C2_AllLayers') || '[]');
  }
  catch
  {
    /* ignore */
  }

  let layersToHide: any[] = [];
  try
  {
    layersToHide = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
  }
  catch
  {
    /* ignore */
  }

  return {
    data,
    allLayers,
    layersToHide,
    layerId: parseInt(localStorage.getItem('C2_layerid') || '0'),
    conv_maxAllowedUpload: 20480000,
    enableChat: localStorage.getItem('C2_enableChat') === 'true',
    personid: parseInt(localStorage.getItem('C2_personid') || '0'),
  };
};

// Menu Configuration
const getConfigMenus = () => ({
  conversation: [
    'goto-project',
    'opendetails-newtab',
    'mark-redflag',
    'follow-conv',
    'copylink-to-clipboard',
  ],
  reply: ['mark-favorite', 'repost', 'delete'],
});

// Real Item Types Configuration
const realItemTypes = {
  Users: { Name: 'People', Icon: 'icons-menu-account-box' },
  Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
};

// User Data
const getUserData = () =>
{
  let personId = 0;
  let userName = 'Test User';
  let userImage = 'Images/user.png?1.0.21.8';
  try
  {
    const data = sessionStorage.getItem('C2_data');
    if (data)
    {
      const parsed = JSON.parse(data);
      personId = parseInt(parsed.personid) || 0;
      userName = parsed.fullname || 'Test User';
      if (parsed.imagename) userImage = parsed.imagename;
    }
  }
  catch
  {
    /* ignore */
  }
  return {
    imageProfile: userImage,
    personName: userName,
    PersonId: personId,
    UserName: userName,
    ImageName: userImage,
  };
};

// Section configuration mapping for Widget API
const WIDGET_SECTION_CONFIG: Record<
  string,
  { title: string; icon: React.ReactNode; order: number }
> = {
  default: { title: 'Widgets', icon: <LayoutGrid className="w-4 h-4" />, order: 1 },
};

// Section configuration mapping for Due API (Today=1, ThisWeek=2, NextWeek=3)
const DUE_SECTION_CONFIG: Record<string, { title: string; icon: React.ReactNode; order: number }> =
  {
    'Due Today': {
      title: 'Due Today',
      icon: <CalendarDays className="w-4 h-4 text-[#f26c4f]" />,
      order: 1,
    },
    'Due This Week': {
      title: 'Due This Week',
      icon: <CalendarDays className="w-4 h-4 text-orange-500" />,
      order: 2,
    },
    'Due Next Week': {
      title: 'Due Next Week',
      icon: <CalendarRange className="w-4 h-4 text-[#7f8f51]" />,
      order: 3,
    },
  };

interface DueTimelineSectionProps {
  searchText: string;
  /** Incremented by the parent to trigger a background refresh */
  refreshToken?: number;
  viewAsPersonId?: string;
}

interface DashboardGroup {
  id: number;
  name: string;
  title: string;
  icon: React.ReactNode;
  conversations: ConversationDetailType[];
  order: number;
}

import { useInfoWindow } from '../../../../contexts/InfoWindowContext';

// ... (existing imports)

export const DueTimelineSection: React.FC<DueTimelineSectionProps> = ({
  searchText,
  refreshToken,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  const [showCompleted, _setShowCompleted] = useState(false);
  void _setShowCompleted;
  const [loading, setLoading] = useState(true);
  const [widgetGroups, setWidgetGroups] = useState<DashboardGroup[]>([]);
  const [dueGroups, setDueGroups] = useState<DashboardGroup[]>([]);
  const { openInfoWindow } = useInfoWindow();

  // Configuration state
  const [api] = useState(() => getApiConfig());
  const [config] = useState(() => getAppConfig());

  useEffect(() =>
  {
    loadData();
  }, [searchText, showCompleted]);

  // Reload when a socket refresh notification arrives from the parent
  useEffect(() =>
  {
    if (refreshToken && refreshToken > 0) loadData();
  }, [refreshToken]);

  // Recargar cuando cambien los layers ocultos
  useEffect(() =>
  {
    const handleLayersChanged = () =>
    {
      loadData();
    };
    window.addEventListener('layersToHideChanged', handleLayersChanged);
    return () => window.removeEventListener('layersToHideChanged', handleLayersChanged);
  }, [searchText, showCompleted]);

  useEffect(() =>
  {
    const unsub = conversationBus.on('redflag-updated', ({ conversationId, isRedFlag }) =>
    {
      const updater = (prev: DashboardGroup[]) =>
        prev.map(group => ({
          ...group,
          conversations: group.conversations.map(conv =>
            conv.ConversationId === conversationId ? { ...conv, IsRedFlag: isRedFlag } : conv,
          ),
        }));
      setWidgetGroups(updater);
      setDueGroups(updater);
    });
    return unsub;
  }, []);

  // Init: Load from cache immediately
  useEffect(() =>
  {
    const parsed = readViewCache<any>(DUETIMELINE_CACHE_KEY);
    if (parsed && (parsed.widgetGroups || parsed.dueGroups))
    {
      // Restore React icons (they can't survive JSON serialization)
      const restoreWidgetIcons = (groups: any[]) =>
        groups.map((g: any) => ({
          ...g,
          icon: WIDGET_SECTION_CONFIG['default']?.icon,
        }));
      const restoreDueIcons = (groups: any[]) =>
        groups.map((g: any) => ({
          ...g,
          icon: (DUE_SECTION_CONFIG[g.name] || { icon: <AlertCircle className="w-4 h-4" /> }).icon,
        }));
      setWidgetGroups(restoreWidgetIcons(parsed.widgetGroups || []));
      setDueGroups(restoreDueIcons(parsed.dueGroups || []));
      setLoading(false);
    }
  }, []);

  const loadData = async () =>
  {
    try
    {
      // Only set loading if no data is present
      setLoading(_prev =>
      {
        if (widgetGroups.length > 0 || dueGroups.length > 0) return false;
        return true;
      });

      // Get PersonId from authStorage (logged in user)
      const personId = authStorage.getPersonId() || '0';

      const params = {
        PageNumber: 1,
        ShowCompleted: showCompleted ? 1 : 0,
        PageSize: 100,
        Me: personId,
        LayersToHide: (() =>
        {
          try
          {
            const arr = JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]');
            return arr.length ? arr.join(',') : '';
          }
          catch
          {
            return '';
          }
        })(),
      };

      // Fetch both APIs in parallel
      const [widgetResponse, dueResponse] = await Promise.all([
        homeService.getHomeConversationsWidget(params),
        homeService.getHomeDueConversationsNew(params),
      ]);

      // Map Widget response - Use Option - Person conjugation for title
      const mappedWidgets: DashboardGroup[] = widgetResponse
        .map((group: DashboardGroupConversationsResponse) =>
        {
          // Build title as "Option - Person" (e.g., "completed - assigned to")
          const widgetTitle =
            group.Option && group.Person
              ? `${group.Option} - ${group.Person}`
              : group.Name || 'Widget';

          const sectionConfig = WIDGET_SECTION_CONFIG['default']!;

          return {
            id: group.DashboardGroupId,
            name: group.Name,
            title: widgetTitle, // Dynamic Title
            icon: sectionConfig.icon,
            conversations: group.Conversations || [],
            order: sectionConfig.order,
          };
        })
        .sort((a, b) => a.order - b.order);

      // Group flat conversation list by Today field (1=Due Today, 2=Due This Week, 3=Due Next Week)
      // matching legacy LoadHomeDueConversations behavior
      const todayMap: Record<number, { key: string; id: number }> = {
        1: { key: 'Due Today', id: 1 },
        2: { key: 'Due This Week', id: 2 },
        3: { key: 'Due Next Week', id: 3 },
      };
      const buckets: Record<string, ConversationDetailType[]> = {
        'Due Today': [],
        'Due This Week': [],
        'Due Next Week': [],
      };
      for (const conv of dueResponse)
      {
        if (conv.ParentConversationId || (conv as any).HasParent) continue;
        const bucket = todayMap[conv.Today as number];
        if (bucket) buckets[bucket.key]?.push(conv);
      }
      const mappedDue: DashboardGroup[] = Object.entries(buckets)
        .map(([sectionName, convs]) =>
        {
          const sectionConfig = DUE_SECTION_CONFIG[sectionName]!;
          return {
            id: sectionConfig.order,
            name: sectionName,
            title: sectionConfig.title,
            icon: sectionConfig.icon,
            conversations: convs,
            order: sectionConfig.order,
          };
        })
        .sort((a, b) => a.order - b.order);

      setWidgetGroups(mappedWidgets);
      setDueGroups(mappedDue);

      // Icon is restored from config on read
      const forCache = (groups: any[]) =>
        groups.map(({ icon: _icon, ...group }: any) => ({
          ...group,
          conversations: (group.conversations || []).slice(0, DUETIMELINE_CACHE_LIMIT),
        }));

      writeViewCache(DUETIMELINE_CACHE_KEY, {
        widgetGroups: forCache(mappedWidgets),
        dueGroups: forCache(mappedDue),
      });
    }
    catch (error)
    {
      console.error('Error loading due/timeline:', error);
    }
    finally
    {
      setLoading(false);
    }
  };

  // Build the request object for ConversationDetailsView with isViewCard=false for title view
  const buildRequest = (conversationId: number, conversation?: ConversationDetailType) => ({
    api: api,
    enableLoading: false,
    showErrorMessage: true,
    isInfoWindow: false,
    isInfowindowViewCard: false,
    hasArchivedFilter: false,
    isConversationDetailsView: true,
    hideCustomFields: true,
    isViewCard: false, // Title view mode
    hideActionMenu: true, // Hide 3-dots menu in Home columns
    isHomeView: true,
    config: config,
    userData: getUserData(),
    storageData: getStorageData(),
    params: {
      ConversationId: conversationId,
      SocketConnectionId: socketConnectionId || undefined,
      IsFromProject: false,
      ConfigMenus: getConfigMenus(),
      ShowRefreshButton: true,
    },
    functions: {
      onLoadDataCallback: () => {},
      onSendError: () => {},
      loadingBarStart: () => {},
      loadingBarComplete: () => {},
      openInfoWindow: (conv: any) =>
      {
        // Callers pass either a plain id or the conversation object
        const id = typeof conv === 'number' ? conv : conv?.ConversationId;
        if (id) openInfoWindow(Number(id));
      },
      attachReactFunctions: () => {},
      onUpdateConversation: () => {},
      onAddReply: () => {},
      onUpdateReply: () => {},
      onDeleteReply: (_e: unknown, callback: (() => void) | undefined) =>
      {
        if (callback) callback();
      },
      onCompleteToggle: () => {},
      markRepost: () => {},
      markFavorite: () => {},
      markRedflag: () => {},
      editReply: () => {},
      deleteReply: () => {},
      archiveConversation: (_e: unknown, _conv: unknown, callback: (() => void) | undefined) =>
      {
        if (callback) callback();
      },
      destroy: () => {},
      saveSearchResult: () => {},
      copyTo: () => {},
      onReplyDeleted: () => {},
      onCreateItemFromCustomType: () => {},
      onItemConvertTo: () => {},
      onChangedProject: () => {},
      onChangedSecurityGroups: () => {},
      getContactCard: () => {},
      showRecentlyNotification: () => {},
      onDuplicateItem: () => {},
    },
    realItemTypes: realItemTypes,
    conversation: conversation
      ? ({ ...conversation, id: String(conversationId) } as any)
      : undefined,
  });

  // Render a group section (shared between widgets and due)
  const renderGroup = (group: DashboardGroup, index: number) =>
  {
    if (group.conversations.length === 0) return;
    return (
      <div key={`${group.id}-${index}`} className="due-group mb-4">
        {/* Section Header */}
        <div className="flex items-center gap-2 ">
          <span className="text-gray-500">{group.icon}</span>
          <h3 className="text-sm font-bold text-gray-500">{group.title}</h3>
        </div>

        {/* Conversations List */}
        <div className="bg-white rounded shadow-sm" style={{ overflowY: 'auto', maxHeight: '200px' }}>
          {group.conversations.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <p className="text-sm">No items in this section</p>
            </div>
          ) : (
            <ul className="due-conversation-list list-none p-0 m-0 divide-y divide-gray-100">
              {group.conversations.map((conv, idx) => (
                <li key={`${conv.ConversationId}-${idx}`} className="due-conversation-item">
                  <ConversationDetailsView request={buildRequest(conv.ConversationId, conv)} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  if (loading)
  {
    return (
      <div className="due-timeline-section h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-3 px-2">
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded shadow-sm p-3">
                <div className="h-4 bg-gray-200 rounded w-32 mb-3 animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
                  <div className="h-8 bg-gray-100 rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="due-timeline-section h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Header with Show Completed checkbox */}

        {/* Widget Groups (First) */}
        {widgetGroups.length > 0 && (
          <div className="widget-groups-container mb-4">{widgetGroups.map(renderGroup)}</div>
        )}
        {/* Due Groups (After Widgets) */}
        {dueGroups.length > 0 && (
          <div className="due-groups-container">{dueGroups.map(renderGroup)}</div>
        )}

      </div>
    </div>
  );
};
